import { assign, createMachine } from 'xstate'
import { Context, Event, EventId, Options, Props, State, StateId } from './Types'
import { useMachine } from '@xstate/react'
import { ourUuid } from 'utils'
import { useCreateSchedule } from 'hooks/queries/brandKits/useCreateSchedule'
import { useModals } from 'almanac-ui/Modal'
import { generatePath, useHistory } from 'react-router-dom'
import { BRAND_KITS_PATH, CAMPAIGN_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ApiError } from 'utils/error'
import { useQueryClient } from 'react-query'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { MediaObject, AnchorContentType } from 'types'
import { YoutubeLinkError } from 'hooks/queries/useCreateAnchorContent'
import { usePrepareBatchCampaigns } from 'hooks/queries/schedules/usePrepareBatchCampaigns'
import { CrosspostStrategyInput } from 'hooks/queries/useUpdateBrandPlan'
import {
  mergeCrosspostDefaults,
  mergeEmailDefaults,
  mergeFacebookDefaults,
  mergeGoogleBusinessProfileDefaults,
  mergeInstagramDefaults,
  mergeLinkedInDefaults,
  mergeSeoDefaults,
  mergeTiktokDefaults,
  mergeTwitterDefaults,
  mergeVideoCrosspostDefaults,
  mergeYoutubeDefaults,
} from 'utils/brandPlans/mergeDefaultContents'

const YOUTUBE_PATTERN = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/i

const addCampaignMachine = createMachine<Context, Event, State>({
  id: 'createNewCampaign',
  initial: 'CreateCampaign',
  on: {
    [EventId.PrepareCampaign]: {
      target: StateId.Analysing,
      actions: [
        assign({
          sources: (_context, event) => event.sources,
          topic: (_context, event) => event.topic,
          start_date: (_context, event) => event.startDate,
          length: (_context, event) => event.length,
          anchor_content: (context, event) => event.anchorContent || context.anchor_content,
          transient: (context, event) => ({
            ...context.transient,
            file: event.file,
          }),
          strategy: (context, event) => ({
            ...context.strategy,
            target_link: event.targetLink,
          }),
        }),
      ],
    },
    [EventId.AnchorContentUploaded]: {
      actions: assign({
        anchor_content: (context, event) => event.anchorContent,
      }),
    },
    [EventId.SourceUploaded]: {
      actions: assign({
        sources: (context, event) => [...context.sources, event.source],
      }),
    },
  },
  states: {
    [StateId.CreateCampaign]: {
      meta: {
        title: 'Create New Campaign',
      },
      on: {
        [EventId.Back]: {
          actions: 'close',
        },
      },
    },
    [StateId.Analysing]: {
      entry: 'prepareCampaign',
      exit: assign({
        transient: (context) => {
          context.transient.abortController.abort()
          return {
            ...context.transient,
            abortController: new AbortController(),
          }
        },
      }),
      meta: {
        title: 'Reviewing Target Link',
      },
      on: {
        [EventId.Back]: StateId.CreateCampaign,
        [EventId.PreparationComplete]: {
          target: StateId.Review,
          actions: assign({
            audience: (context, event) => event.preparation.result?.[0]?.audience || '',
            goal: (context, event) => event.preparation.result?.[0]?.goal || '',
            cover_image: (context, event) => event.preparation?.result?.[0].cover_image || '',
            strategy: (context, event) => {
              return {
                ...context.strategy,
                ...(event.preparation.result?.[0]?.strategy as CrosspostStrategyInput),
              }
            },
            crosspost_contents: (context, event) =>
              mergeCrosspostDefaults(event.preparation?.result?.[0].crosspost_contents),
            video_crosspost_contents: (context, event) =>
              mergeVideoCrosspostDefaults(event.preparation?.result?.[0].video_crosspost_contents),
            instagram_contents: (context, event) =>
              mergeInstagramDefaults(event.preparation?.result?.[0].instagram_contents),
            facebook_contents: (context, event) =>
              mergeFacebookDefaults(event.preparation?.result?.[0].facebook_contents),
            linkedin_contents: (context, event) =>
              mergeLinkedInDefaults(event.preparation?.result?.[0].linkedin_contents),
            twitter_contents: (context, event) => mergeTwitterDefaults(event.preparation?.result?.[0].twitter_contents),
            youtube_contents: (context, event) => mergeYoutubeDefaults(event.preparation?.result?.[0].youtube_contents),
            tiktok_contents: (context, event) => mergeTiktokDefaults(event.preparation?.result?.[0].tiktok_contents),
            seo_contents: (context, event) => mergeSeoDefaults(event.preparation?.result?.[0].seo_contents),
            email_contents: (context, event) => mergeEmailDefaults(event.preparation?.result?.[0].email_contents),
            google_my_business_contents: (context, event) =>
              mergeGoogleBusinessProfileDefaults((event.preparation?.result?.[0] as any).google_my_business_contents),
          }),
        },
        [EventId.Error]: {
          target: StateId.AnalysisError,
          actions: assign({
            error: (context, event) => event.error,
          }),
        },
      },
    },
    [StateId.AnalysisError]: {
      meta: {
        title: 'Link Review Issue',
      },
      on: {
        [EventId.Back]: StateId.CreateCampaign,
      },
    },
    [StateId.Review]: {
      meta: {
        title: 'Review Our Findings',
      },
      on: {
        [EventId.Back]: StateId.CreateCampaign,
        [EventId.ReviewComplete]: {
          target: StateId.Distribution,
          actions: assign({
            audience: (context, event) => event.audience,
            goal: (context, event) => event.goal,
            strategy: (context, event) => {
              const theme = ourUuid()
              return {
                ...context.strategy,
                themes: [
                  {
                    id: theme,
                    title: event.theme,
                    description: event.description,
                  },
                ],
                theme,
                target_link: event.targetLink,
                call_to_action: event.callToAction,
              }
            },
          }),
        },
      },
    },
    [StateId.Distribution]: {
      meta: {
        title: 'Select Channels for your campaign',
      },
      on: {
        [EventId.Back]: StateId.Review,
        [EventId.Finish]: {
          actions: [
            assign({
              instagram_contents: (_context, event) => event.instagram_contents,
              facebook_contents: (_context, event) => event.facebook_contents,
              linkedin_contents: (_context, event) => event.linkedin_contents,
              twitter_contents: (_context, event) => event.twitter_contents,
              email_contents: (_context, event) => event.email_contents,
              seo_contents: (_context, event) => event.seo_contents,
              tiktok_contents: (_context, event) => event.tiktok_contents,
              youtube_contents: (_context, event) => event.youtube_contents,
              crosspost_contents: (_context, event) => event.crosspost_contents,
              video_crosspost_contents: (_context, event) => event.video_crosspost_contents,
              google_my_business_contents: (_context, event) => event.google_my_business_contents,
              crosspost_frequency: (_context, event) => event.crosspost_frequency,
              tactics: (_context, event) => event.tactics,
            }),
            'createCampaign',
          ],
        },
      },
    },
  },
})

export const useAddCampaignMachine = (props: Props, options: Options) => {
  const { brandPlanId } = props
  const { closeAllModals } = useModals()
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { mutate: createCampaign } = useCreateSchedule(
    { brandPlanId },
    {
      onSuccess: async (data) => {
        closeAllModals()
        await queryClient.invalidateQueries(['workspaces', workspaceId, 'schedules'])
        history.push(generatePath(CAMPAIGN_PATH, { workspaceId, campaignId: data.schedule.id }))
      },
      onError: (error) => {
        if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
          const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
          showToastMessage(
            'Your content preferences require brand kit media, but none are uploaded.',
            'error',
            { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
            10,
          )
        } else {
          showErrorMessageToast('There was a problem creating your campaign. Please try again.')
        }
        send({ type: EventId.Error, error })
      },
    },
  )
  const { mutate: prepareBatch } = usePrepareBatchCampaigns(
    { brandPlanId },
    {
      onSuccess: (data) => {
        send({ type: EventId.PreparationComplete, preparation: data })
      },
      onError: (error) => {
        send({ type: EventId.Error, error })
      },
    },
  )

  const { mutateAsync: createMediaObjects } = useCreateMediaObjects({
    onError: (error) => {
      send({ type: EventId.Error, error })
    },
  })

  const { mutateAsync: createSource } = useCreateSource({
    onError: (error) => {
      send({ type: EventId.Error, error })
    },
  })

  const machine = useMachine<Context, Event, State>(addCampaignMachine, {
    ...options,
    actions: {
      ...options.actions,
      prepareCampaign: async (ctx) => {
        let { anchor_content } = ctx

        if (ctx.transient.file) {
          const mediaObjectsResult = await createMediaObjects([
            {
              file: ctx.transient.file,
              filename: ctx.transient.file.name,
            },
          ])

          send({
            type: EventId.AnchorContentUploaded,
            anchorContent: {
              type: 'MediaObject',
              ...mediaObjectsResult[0],
            },
          })

          const source = await createSource({
            media_object_id: mediaObjectsResult[0].id,
            type: 'SourceMaterialsMediaObject',
          })

          send({
            type: EventId.SourceUploaded,
            source: source.source,
          })

          anchor_content = {
            type: 'MediaObject',
            id: mediaObjectsResult[0].id,
          } as MediaObject & { type: AnchorContentType }
        }

        if (!anchor_content) {
          if (YOUTUBE_PATTERN.test(ctx.strategy.target_link)) {
            send({
              type: EventId.Error,
              error: new YoutubeLinkError('Unable to scrape YouTube'),
            })
            return
          }

          const source = await createSource({
            url: ctx.strategy.target_link,
            type: 'Website',
          })

          send({
            type: EventId.AnchorContentUploaded,
            anchorContent: source.source,
          })

          send({
            type: EventId.SourceUploaded,
            source: source.source,
          })
        }

        prepareBatch({
          start_date: ctx.start_date,
          campaigns: [
            {
              topic: ctx.topic,
              start_date: ctx.start_date,
              length: ctx.length,
            },
          ],
          signal: ctx.transient.abortController.signal,
        })
      },
      createCampaign: (ctx) => {
        const { transient, ...rest } = ctx
        createCampaign(rest)
      },
    },
  })

  const [, send] = machine

  return machine
}
